"use client";

import { composeRefs } from "@radix-ui/react-compose-refs";
import type { FieldPath, FieldValues, UseControllerProps } from "react-hook-form";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "..";
import { Textarea as DumbArea, type TextareaProps } from "../../Textarea";
import type { FormFieldProps } from "../types";

export const Textarea = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  label,
  description,
  optional,
  placeholder,
  className,
  inputClassName,
  name,
  control,
  shouldUnregister,
  inputRef,
  minLength,
  ...inputProps
}: FormFieldProps<TFieldValues, TName, never, HTMLTextAreaElement> &
  TextareaProps & { inputClassName?: string; minLength?: number }) => {
  const formFieldProps: UseControllerProps<TFieldValues, TName> = { name, control, shouldUnregister };

  return (
    <FormField
      {...formFieldProps}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <FormLabel optional={optional}>{label}</FormLabel>
          <FormControl>
            <DumbArea
              className={inputClassName}
              placeholder={placeholder}
              {...inputProps}
              {...field}
              ref={composeRefs(inputRef, field.ref)}
              value={field.value ?? ""}
              invalid={!!fieldState.error}
              onBlur={(e) => {
                field.onBlur();
                inputProps.onBlur?.(e);
              }}
            />
          </FormControl>
          {(minLength || inputProps.maxLength) && (
            <FormDescription>
              {minLength && (field.value?.length ?? 0) < minLength
                ? `${field.value?.length ?? 0}/${minLength} characters minimum`
                : `${field.value?.length ?? 0}/${inputProps.maxLength} characters`}
            </FormDescription>
          )}
          {!!description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

Textarea.displayName = "Textarea";
